import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Button, { IButtonProps } from '../../../components/bootstrap/Button';
import Dropdown, { DropdownMenu, DropdownToggle } from '../../../components/bootstrap/Dropdown';
import Icon from '../../../components/icon/Icon';
import useDarkMode from '../../../hooks/useDarkMode';
import { useShoppingCart } from '../../../contexts/shoppingCartContext';
import AuthContext from '../../../contexts/authContext';
import { clearAllAppData } from '../../../helpers/authHelpers';
import AccountMenuItems from './AccountMenuItems';

/**
 * Sign-in/account + cart, rendered directly in the persistent compact mobile
 * header bar (see Header's `mobileEnd` prop). Everything else in the nav
 * (Home, Products, dark mode, language) stays behind the hamburger menu, but
 * these two shouldn't be — a shopper needs to always be able to see/reach
 * their cart and sign in without an extra tap.
 */
const MobileHeaderActions = () => {
	const { darkModeStatus } = useDarkMode();
	const { user, setUser } = useContext(AuthContext);
	const loggedIn = !!user && !!localStorage.getItem('token');
	const { cartQuantity } = useShoppingCart();
	const navigate = useNavigate();

	const styledBtn: IButtonProps = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	const handleSignOut = () => {
		clearAllAppData();
		if (setUser) setUser('');
		window.location.href = '/';
	};

	return (
		<>
			{loggedIn ? (
				<Dropdown>
					<DropdownToggle hasIcon={false}>
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							className='btn-only-icon'
							aria-label='My account'>
							<Icon icon='AccountCircle' color={darkModeStatus ? 'light' : 'dark'} className='btn-icon' />
						</Button>
					</DropdownToggle>
					<DropdownMenu isAlignmentEnd size='lg' className='py-0 overflow-hidden'>
						<AccountMenuItems darkModeStatus={darkModeStatus} navigate={navigate} onSignOut={handleSignOut} />
					</DropdownMenu>
				</Dropdown>
			) : (
				<Button
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...styledBtn}
					className='btn-only-icon'
					aria-label='Sign in'
					onClick={() => navigate('/auth')}>
					<Icon icon='AccountCircle' color={darkModeStatus ? 'light' : 'dark'} className='btn-icon' />
				</Button>
			)}

			<Button
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...styledBtn}
				className='btn-only-icon position-relative'
				aria-label='Open cart'
				id='header-cart-icon-mobile'
				onClick={() => navigate('/my-cart')}>
				<Icon icon='ShoppingCart' color={darkModeStatus ? 'light' : 'dark'} className='btn-icon' />
				{cartQuantity > 0 && (
					<span
						className='position-absolute badge rounded-pill bg-danger'
						style={{
							top: 4,
							right: 2,
							fontSize: 10,
							fontWeight: 700,
							minWidth: 18,
							height: 18,
							lineHeight: '18px',
							padding: '0 5px',
						}}>
						{cartQuantity}
					</span>
				)}
			</Button>
		</>
	);
};

export default MobileHeaderActions;
